import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { AlomainLogo } from "@/shared/ui";
import { LocationPickerHost } from "@/features/location";
import { useIsTabletUp, useIsWideUp } from "@/shared/hooks/useMediaQuery";
import { AsideTargetProvider, useAsideTarget } from "./ShellAside";
import type { NavItem } from "./navItems";
import { paths } from "../routes/paths";

function Sidebar({ navigation }: { navigation: NavItem[] }) {
  return (
    <nav
      aria-label="Navigation principale"
      className="flex w-sidebar shrink-0 flex-col gap-1 border-r border-gray-100 bg-white px-4 py-6"
    >
      <div className="mb-6 flex items-center gap-2 px-2">
        <AlomainLogo height={24} />
        <span className="font-extrabold text-xl text-navy-800">Alomain</span>
      </div>

      {navigation.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/" || to === paths.agentHome()}
          className={({ isActive }) =>
            [
              "flex items-center gap-3 rounded-btn px-3 py-2.5 font-medium transition-colors",
              isActive
                ? "bg-or-50 text-or-700 font-bold"
                : "text-gray-500 hover:bg-gray-50 hover:text-navy-800",
            ].join(" ")
          }
        >
          <Icon size={20} aria-hidden />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function TabBar({ navigation }: { navigation: NavItem[] }) {
  return (
    <nav
      aria-label="Navigation principale"
      className="flex shrink-0 items-stretch border-t border-gray-100 bg-white pb-[var(--safe-bottom)]"
    >
      {navigation.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/" || to === paths.agentHome()}
          className={({ isActive }) =>
            [
              "flex flex-1 flex-col items-center gap-1 px-1 pb-2 pt-1.5 text-[11px] font-bold transition-colors",
              isActive ? "text-or-700" : "text-gray-500",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              {/* Pastille or pâle sous l'icône active — reprise du design natif. */}
              <span
                className={[
                  "flex h-7 w-12 items-center justify-center rounded-xs transition-colors",
                  isActive ? "bg-or-50" : "",
                ].join(" ")}
              >
                <Icon size={20} aria-hidden />
              </span>
              <span>{label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Structure partagée : un seul défilement central et les mêmes seuils responsive.
 * Les shells choisissent leurs données, leur en-tête et leur navigation selon le rôle.
 */
export function ApplicationLayout({
  navigation,
  header,
  aside,
  children,
  contentClassName = "relative mx-auto h-full w-full lg:max-w-content lg:px-8 lg:py-8",
}: {
  navigation: NavItem[];
  header?: ReactNode;
  aside: ReactNode;
  children: ReactNode;
  contentClassName?: string;
}) {
  const isTabletUp = useIsTabletUp();
  const isWideUp = useIsWideUp();
  const [asideEl, setAsideEl] = useAsideTarget();
  return (
    <AsideTargetProvider target={isWideUp ? asideEl : null}>
      <div className="flex h-dvh overflow-hidden bg-white">
        {isTabletUp && <Sidebar navigation={navigation} />}
        <div
          className={`flex min-w-0 flex-1 flex-col ${isTabletUp ? "" : "pt-[var(--safe-top)]"}`}
        >
          {header}
          <div className="relative flex min-h-0 flex-1">
            <main className="min-w-0 flex-1 overflow-y-auto">
              <div className={contentClassName}>
                {children}
                <LocationPickerHost />
              </div>
            </main>
            {isWideUp && (
              <aside
                ref={setAsideEl}
                aria-label="Informations contextuelles"
                className="w-aside shrink-0 overflow-y-auto border-l border-gray-100 bg-gray-50"
              >
                {aside}
              </aside>
            )}
          </div>
          {!isTabletUp && <TabBar navigation={navigation} />}
        </div>
      </div>
    </AsideTargetProvider>
  );
}
